import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, KeyRound, LoaderCircle, RefreshCw, Workflow, Zap } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_STATUSES, useAuth } from "../contexts/AuthContext";
import { AuthCard, AuthFullscreenState, AuthNotice } from "../components/AuthComponents";
import ThemeToggle from "../components/ThemeToggle";

const pageShellClassName =
  "relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 px-6 py-10 text-zinc-50";

const PageShell = ({ children }) => (
  <div className={pageShellClassName}>
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_26%),radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.07),transparent_18%),linear-gradient(135deg,rgba(255,255,255,0.03),transparent_55%)]" />
    <div className="pointer-events-none absolute inset-0 opacity-50 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:18px_18px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
    <div className="absolute right-6 top-6 z-20">
      <ThemeToggle />
    </div>
    <div className="relative z-10 w-full max-w-sm">{children}</div>
  </div>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = useMemo(
    () => (typeof location.state?.from === "string" ? location.state.from : "/dashboard"),
    [location.state],
  );

  const {
    loading,
    error,
    authStatus,
    isAuthenticated,
    localAuthEnabled,
    localAuthProfile,
    loginWithLocalBypass,
    loginWithPasscode,
    checkAuth,
    clearError,
  } = useAuth();

  const [name, setName] = useState(localAuthProfile.name);
  const [email, setEmail] = useState(localAuthProfile.email);
  const [passcode, setPasscode] = useState("");

  useEffect(() => {
    setName(localAuthProfile.name);
    setEmail(localAuthProfile.email);
  }, [localAuthProfile.email, localAuthProfile.name]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const accessError = location.state?.authError || error;

  const handleLocalAccess = async (event) => {
    event.preventDefault();
    clearError();

    const result = await loginWithLocalBypass({
      name: name.trim() || localAuthProfile.name,
      email: email.trim() || localAuthProfile.email,
    });

    if (result?.ok) {
      navigate(redirectTo, { replace: true });
    }
  };

  const handlePasscodeAccess = async (event) => {
    event.preventDefault();
    clearError();

    const result = await loginWithPasscode(passcode.trim());

    if (result?.ok) {
      navigate(redirectTo, { replace: true });
    }
  };

  if (loading) {
    return (
      <AuthFullscreenState
        title="Checking access"
        description="Verifying your secure session before we open Venture Copilot."
      />
    );
  }

  return (
    <PageShell>
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-zinc-900 shadow-sm">
          <Zap className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-light text-zinc-50 font-['Outfit']">Venture Copilot</h1>
        <p className="mt-2 text-sm text-zinc-400 font-['Manrope']">
          Invite-only access with backend-backed sessions.
        </p>
      </div>

      <AuthCard
        icon={localAuthEnabled ? Workflow : KeyRound}
        eyebrow={localAuthEnabled ? "Local development access" : "Private access"}
        title={localAuthEnabled ? "Continue into the local workspace" : "Enter your access code"}
        description={
          localAuthEnabled
            ? "This environment is using the backend's local auth bypass. We'll create a short-lived session cookie instead of storing a fake user in localStorage."
            : "Use your entry code to create a secure backend session, or open the app with a session-enabled invite link."
        }
      >
        <div className="space-y-4">
          {accessError && authStatus !== AUTH_STATUSES.UNAVAILABLE ? <AuthNotice tone="danger">{accessError}</AuthNotice> : null}

          {authStatus === AUTH_STATUSES.UNAVAILABLE ? (
            <>
              <AuthNotice tone="danger">
                {accessError || "Authentication is currently unavailable. This usually means the backend is offline or the session endpoint is unreachable."}
              </AuthNotice>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    clearError();
                    void checkAuth();
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry session check
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/", { replace: true })}
                  className="flex-1 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                >
                  Back to home
                </button>
              </div>
            </>
          ) : localAuthEnabled ? (
            <form onSubmit={handleLocalAccess} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Display name</label>
                <input
                  type="text"
                  value={name}
                  autoComplete="name"
                  onChange={(event) => {
                    setName(event.target.value);
                    clearError();
                  }}
                  placeholder="Local Demo User"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Email</label>
                <input
                  type="email"
                  value={email}
                  autoComplete="email"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearError();
                  }}
                  placeholder="local-demo@venturecopilot.dev"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                />
              </div>

              <AuthNotice tone="neutral">
                Requires both `REACT_APP_ENABLE_LOCAL_AUTH_BYPASS=true` in the frontend and `LOCAL_AUTH_BYPASS=true`
                in the backend.
              </AuthNotice>

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
              >
                Continue to dashboard
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasscodeAccess} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Entry code</label>
                <input
                  type="password"
                  value={passcode}
                  autoComplete="one-time-code"
                  onChange={(event) => {
                    setPasscode(event.target.value);
                    clearError();
                  }}
                  placeholder="Enter your access code"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm tracking-[0.08em] text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                />
              </div>

              <AuthNotice tone="neutral">
                Entry codes create backend-backed sessions. If you already have a secure invite link, opening it still
                works too.
              </AuthNotice>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="submit"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                >
                  Continue to dashboard
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearError();
                    void checkAuth();
                  }}
                  className="flex-1 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                >
                  Retry session check
                </button>
              </div>
            </form>
          )}
        </div>
      </AuthCard>
    </PageShell>
  );
}
